<template>
  <v-app id="app">
    <router-view/>
    <LModal/>
    <keep-alive>
      <PreloadedV3Iframe/>
    </keep-alive>
  </v-app>
</template>

<script setup lang="ts">
import EnvironmentTheme from './env/services/EnvironmentTheme';
import {setupTheme} from './services/theme/ThemeService';
import LModal from '@/components/L/Modal/LModal.vue';
import PreloadedV3Iframe from '@/components/PreloadedV3Iframe.vue';

setupTheme(EnvironmentTheme.LENDI);
</script>

<style lang="scss">
@import "./assets/fonts/fonts.css";
@import "./assets/styles/main.scss";
@import './assets/styles/vuetifyOverride.scss';
@import './assets/styles/colorsTokens.theme.scss';

.row:not([class*="my-"]):not([class*="ma-"]):not([class*="mt-"]):not([class*="mb-"]) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

* {
  scrollbar-width: thin;
}

/*
 * Training/accreditation flows (statements, bank statements, KNF, ethics, BHP) render
 * `base-navigation` with no #bar slot, so on md+ there is no top app-bar and Vuetify's
 * v-main has 0 top-padding. The shared sticky `.navigation-header-container` uses top: 56px
 * (to clear a 56px app-bar), which here leaves a 56px gap above it: scrolled content shows
 * in that gap and the header sits 56px down over the first question. With no bar present the
 * header must stick to the very top. On <960px BaseNavigation does render a 56px app-bar, so
 * the override is scoped to md+ where there is none.
 */
@media (min-width: 960px) {
  .accreditation-quiz-page .navigation-header-container {
    top: 0 !important;
  }
}
::-webkit-scrollbar {
  width: 8px;
  max-width: 8px;
  height: 8px;
}
@supports (scrollbar-color: auto) {
  * {
    scrollbar-color: color(from var(--token-core-onCore-highEmphasis) srgb r g b / 0.5) var(--token-core-surface);
  }
}

::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.4);
  -webkit-box-shadow: inset 1px 1px 2px onCoreAlpha(.1);
}
::-webkit-scrollbar-thumb {
  background: #CCC;
  border-radius: 30px;
  -webkit-box-shadow: inset 1px 1px 2px onCoreAlpha(.2);
}
::-webkit-scrollbar-thumb:hover {
  background: #AAA;
}
::-webkit-scrollbar-thumb:active {
  background: #888;
  -webkit-box-shadow: inset 1px 1px 2px onCoreAlpha(.3);
}

.v-input--switch--flat {
  .v-input--switch__thumb {
    border: 1px solid var(--token-core-border) !important;
  }
}

.v-btn:not(.v-btn--icon):not(.v-btn--floating):not(.v-btn--round):not(.v-btn--contained){
  padding: 0 12px;
  text-transform: none;
  font-size: 16px;
  letter-spacing: 0;
  min-width: 44px;
}

div:not(.v-btn-toggle) > .v-btn:not(.v-btn--icon):not(.v-btn--floating):not(.v-btn--round):not(.v-btn--contained){
  border-radius: 4px;
}

.v-list-item .v-list-item__title {
  word-wrap: break-word;
  text-overflow: clip;
  white-space: normal;
}

.v-card__title {
  .v-btn {
    margin: 0;
  }
}

.disabled-text {
  opacity: $disabled-opacity !important;
}

.loading-text {
  opacity: 0.4!important;
}
.secondary-icon {
  opacity: 0.6!important;
}

.disabled-background{
  position: relative;
  &:before{
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background-color: onCoreAlpha(.03);
  }
}

.disabled-img {
  filter: opacity($disabled-opacity);
}

.primary-text-color {
  color: var(--token-core-onCore-highEmphasis) !important;
}

/*Table Styles*/
tbody > a {
  display: table-row;
  vertical-align: inherit;
  border-color: inherit;
  text-decoration: none!important;
  position: relative;
  & > td {
    position: relative;
    &:before {
      content: '';
      position: absolute;
      width: 100%;
      bottom: 0;
      left: 0;
      border-bottom: 1px solid var(--token-core-border);
    }
  }
}

table.v-datatable {

  td > .v-list {
    background: transparent;
    padding: 0;
    margin: -10px 0;
    .v-list-item__title {
      font-size: 13px;
    }
  }
  tbody > tr:hover:not(.v-datatable__expand-row) td,
  tbody > a:hover:not(.v-datatable__expand-row) td{
    background: var(--token-core-border);
  }
  :deep(td) {
    padding: 4px 16px!important;
    font-size: 14px;
  }
  :deep(td),
  :deep(th) {
    background: var(--token-core-surface);
    a {
      color: #222d3a;
      transition: color .15s ease-out;
      &:hover {
        color: var(--token-primary-base);
      }
    }
    & {
      position: sticky;
      transition: background-color 0.2s ease-in;
    }
    @media screen and (max-width: 960px) {
      position: static;
    }
  }
  th:first-child {
     border-top-left-radius: 16px!important;
   }
  }
  .ba{
    border: 1px solid var(--token-core-border);
  }

  .bb-hard {
    position: relative;
    &:before {
      content: "";
      position: absolute;
      width: 100%;
      border-bottom: 1px solid var(--token-core-border);
      height: 1px;
      bottom: 0;
      left: 0;
      z-index: 7;
    }
  }
  .by{
    border-top: 1px solid var(--token-core-border);
    border-bottom: 1px solid var(--token-core-border);
  }
  .bt{
    border-top: 1px solid var(--token-core-border);
  }
  .bb{
    border-bottom: 1px solid var(--token-core-border);
  }
  .br{
    border-right: 1px solid var(--token-core-border);
  }
  .bl{
    border-left: 1px solid var(--token-core-border);
  }
  .bx{
    border-right: 1px solid var(--token-core-border);
    border-left: 1px solid var(--token-core-border);
  }
  .border-bold-dashed{
    border-width: 1.5px !important;
    border-style: dashed !important;
  }
  .border-bold-solid{
    border-width: 1.5px !important;
    border-style: solid !important;
    border-color: var(--token-primary-base) !important;
  }
  .fp-border-card {
    border-radius: 12px;
    -webkit-border-radius: 12px;
    -moz-border-radius: 12px;
    border: solid 1px var(--token-core-border) !important;
  }

.multiline-button.v-btn {
  min-height: 36px;
  height: 100%!important;

  .v-btn__content {
    width: 100%;
    white-space: normal;
  }
}

.display-flex {
  display: flex;
}
div ::v-deep .v-card:not(.user-event) {
  border: 1px solid onCoreAlpha(.87);
  -webkit-box-shadow: 0 0 0 0 rgba(0,0,0,.2),0 0 0 0 rgba(0,0,0,.14),0 0 0 0 rgba(0,0,0,0.12)!important;
  -moz-box-shadow: 0 0 0 0 rgba(0,0,0,.2),0 0 0 0 rgba(0,0,0,.14),0 0 0 0 rgba(0,0,0,0.12)!important;
  box-shadow: 0 0 0 0 rgba(0,0,0,.2),0 0 0 0 rgba(0,0,0,.14),0 0 0 0 rgba(0,0,0,0.12)!important;
}

.all-caps-header {
  text-transform: uppercase !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

.v-radio, .v-input--checkbox {
  .v-input--selection-controls__ripple {
    z-index: 7;
  }
  &.v-radio--is-disabled label {
    color: onCoreAlpha($disabled-opacity) !important;
  }
}

.big-padding-bottom {
  padding-bottom: 25vh !important;
}
.warning-card {
  background-color: color-mix(in srgb, var(--token-warning-base) 12%, var(--token-core-surface)) !important;
  border-color: var(--token-warning-base) !important;
  border-radius: 4px !important;
}
.info-card {
  background-color: color-mix(in srgb, var(--token-primary-base) 12%, var(--token-core-surface)) !important;
  border: none !important;
  border-radius: 4px !important;
}

.fp-checkbox label {
  color: var(--token-core-onCore-highEmphasis) !important;
}
.element-transition {
  --element-transition-property: background-color, color, opacity;
  --element-transition-duration: 500ms;

  *, *::before, *::after {
    transition-duration: var(--element-transition-duration, 500ms) !important;
    transition-property: var(--element-transition-property, all) !important;
  }
}
.sticky-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  background-color: var(--token-core-surface);
}

@media print {
  #usercom-widget {
    display: none;
  }
}

:has(.calendar){
  #app{
    overflow: hidden;
    height: 100dvh;
  }
  #usercom-widget {
    display: none;
  }
  .ui-placeholder{
    display: none;
  }
}

@media (max-width: #{map-get($breakpoints, sm)}) {
  :has(.activity-details-menu-wrapper.menuable__content__active ){
    overflow: hidden;
  }
}

.cog-wrapper i {
  animation:spin 2s linear infinite;
}

@keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }

html {
  scroll-behavior: smooth;
}

.delegation-modal-content {
  /* Same vh→dvh pattern as *-h-viewport utilities (complex max/min — no single utility). */
  height: max(80vh, min(752px, 98vh));
  height: max(80dvh, min(752px, 98dvh));
  min-height: max(80vh, min(752px, 98vh));
  min-height: max(80dvh, min(752px, 98dvh));
  max-height: 94vh;
  max-height: 94dvh;
}

.new-client-modal-content {
  height: min(90vh, 700px);
  min-height: min(90vh, 700px);
  max-height: 90vh !important;
}

.iframe-modal-fullscreen {
  /* Mirrors h-/w-/max-*-viewport utilities with !important (unlayered — beats Vuetify). */
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  width: 100dvw !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-width: 100vw !important;
  max-width: 100dvw !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  margin: 0 !important;
  border-radius: 0 !important;
  transform: none !important;
}
.confirm-modal-card {
  @media (max-width: #{map-get($breakpoints, sm)}) {
    .v-card__actions {
      .v-btn {
        flex-basis: 50%;
        min-height: 36px;
        height: auto;
        .v-btn__content {
          max-width: 100%;
          padding: 6px 0;
          white-space: normal;
        }
      }
    }
  }
}
</style>
